/**
 * Copyright Open Logistics Foundation
 *
 * Licensed under the Open Logistics Foundation License 1.3.
 * For details on the licensing terms, see the LICENSE file.
 * SPDX-License-Identifier: OLFL-1.3
 */

import { Pipe, PipeTransform } from '@angular/core';

/**
 * Minimal shape needed to derive the current plate. Kept structural (rather than
 * importing VehicleConfiguration) so it also accepts a reactive FormArray's
 * `.value`, whose fields are optional because controls may be disabled.
 */
interface VehicleConfigurationLike {
    timestamp?: Date | string | null;
    motorVehicleLicensePlate?: string | null;
}

/**
 * Derives the "current" license plate from a vehicle configuration history:
 * the motorVehicleLicensePlate of the entry with the latest timestamp.
 * Ties (e.g. same-day, date-only entries) are broken by later array index.
 */
@Pipe({ name: 'latestMotorPlate' })
export class LatestMotorPlatePipe implements PipeTransform {
    transform(
        history: VehicleConfigurationLike[] | null | undefined
    ): string {
        if (!history?.length) {
            return '';
        }

        let latest: VehicleConfigurationLike | null = null;
        let latestTime = -Infinity;

        for (const entry of history) {
            const time = entry?.timestamp
                ? new Date(entry.timestamp).getTime()
                : -Infinity;
            // `>=` so that a later array index wins on equal timestamps.
            if (time >= latestTime) {
                latestTime = time;
                latest = entry;
            }
        }

        return latest?.motorVehicleLicensePlate ?? '';
    }
}
